import { Store } from '@ngrx/store';
import { IController } from 'angular';
import { IState } from 'angular-ui-router';
import * as _ from 'lodash';
import { Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { SupportCategories } from '../../../../../../../ng2/shared/constants/support-categories.constant';
import { ISdcStore } from '../../../../../flux/sdc-store.interface';
import { getCurrentUser } from './../../../../../../../ng2/store/selectors/current-user-selectors';
import { getUsersEntities } from './../../../../../../../ng2/store/selectors/users-selectors';
import { WizardUtils } from './../../../../../../shared/services/wizard-utils/wizard-utils.service';

export class TrackAttendanceCtrl implements IController {
  static $inject = [
    '$scope',
    'UtilitiesService',
    'WizardUtils',
    'Support',
    'Store',
    '$mdToast',
    '$rootScope',
    'Rollbar',
    '$state',
    'SpinnerModal',
    'ImUser',
    'SDCStore',
  ];

  public schoolSupports;
  public currentSchool;
  public mySupportsUrl;
  public supportsByActivityLead;
  public supportsByActivatedLead;
  public activityLeadsWithoutAccess: number;
  public currentUserSubscription: ISubscription;
  private hasGafeEmail = false;
  private noAccessUsers: Array<{ [key: string]: boolean }>;
  public destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public $scope,
    private UtilitiesService: UtilitiesService,
    private WizardUtils: WizardUtils,
    private Support,
    private store: Store<any>,
    private $mdToast,
    private $rootScope,
    private Rollbar,
    private $state,
    private spinnerModal,
    private imUser: ImUser,
    private SDCStore: ISdcStore,
  ) {}

  $onInit(): void {
    const usersSlice$ = this.store.select(getUsersEntities);
    usersSlice$
      .pipe(
        tap(users => {
          this.noAccessUsers = users.reduce((users, user) => {
            const email = this.imUser.getEffectiveEmail(user);
            const noAccess = this.imUser.isNoAccessUser(user);
            if (noAccess) users[email] = noAccess;
            return users;
          }, {});
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.WizardUtils.startListeningAndLoad(this);
    const currentUserSlice$ = this.store.select(getCurrentUser);
    this.currentUserSubscription = currentUserSlice$.pipe(take(1)).subscribe((user: IUser) => {
      if (user.gafeEmail) this.hasGafeEmail = true;
    });
  }

  // In order to copy to clipboard there must be an element that contains the
  // text, then we select the text and issue an execCommand copy to copy the
  // selected text into the users clipboard. For that reason we create a
  // temporary element fill it with text, copy it and quickly remove it. The
  // style and position are there to avoid it being seen by the user.
  copyToClipboard(supportsByActivityLead: { [key: string]: { supportNames: string[]; supports: any } }) {
    const body = this.getBody(supportsByActivityLead);
    const el = document.createElement('textarea');
    el.value = body;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    const toastScope = this.$rootScope.$new();
    toastScope.message = 'Draft email copied to clipboard';
    this.$mdToast
      .show({
        hideDelay: 1000,
        position: 'bottom left',
        template: require('../../../../../../student/stateless-components/stale-student-toast/stale-student-toast.html'),
        scope: toastScope,
      })
      .catch(err => {
        if (err) this.Rollbar.warning(err);
        this.Rollbar.warning('Failed to show toast');
      });
  }

  setUi() {
    this.mySupportsUrl = 'portal.newvisions.org/school/' + this.currentSchool._id + '/my-supports';
    this.supportsByActivityLead = this.getSupportsByActivityLead(this.schoolSupports);
    this.supportsByActivatedLead = this.getSupportsByActivatedLead(this.supportsByActivityLead);
    this.activityLeadsWithoutAccess = _.filter(
      this.supportsByActivityLead,
      (v, email: string) => email.split('_')[0] === 'noAccessUser',
    ).length;
  }

  getSupportsByActivityLead(schoolSupports) {
    const inProgressAndUnstartedSupports = this.Support.getInProgressAndNotStartedSupports(schoolSupports);
    const uniqueActivityLeadDetails = _.reduce(
      inProgressAndUnstartedSupports,
      (result: { [key: string]: { supportNames: string[]; supports: any } }, support: any, i) => {
        const supportDetails = {
          supports: [],
          supportNames: [],
        };
        if (support.category === SupportCategories.REGENTS_PREP.key) {
          let key;
          const activityLeadEmail = this.imUser.getEffectiveEmail(support.activityLead);
          // if activityLead does not have access denoted in the this.noAccessUsers object assign default to filter on,
          // rather than null for all
          key = this.noAccessUsers[activityLeadEmail] ? `noAccessUser_${i}` : activityLeadEmail;
          result[key] = result[key] || supportDetails;
          result[key].supports.push(support);
          result[key].supportNames.push(support.name);
        }
        return result;
      },
      {},
    );
    return uniqueActivityLeadDetails;
  }

  getSupportsByActivatedLead(supportsByActivityLead) {
    return _.pickBy(supportsByActivityLead, (val: object, email: string) => {
      return email.split('_')[0] !== 'noAccessUser';
    });
  }

  countOfUniqueActivityLeads(supportsByActivityLead) {
    return _.keys(supportsByActivityLead).length;
  }

  goState(state: IState): void {
    this.spinnerModal.activateSpinner('Loading...');
    this.$state.go(state);
  }

  joinSupportNames(supportNames) {
    return supportNames.join(', ');
  }

  getHref(supportsByActivityLead) {
    const base = 'https://mail.google.com/mail/?view=cm';
    const to = '&to=' + encodeURIComponent(_.keys(supportsByActivityLead).join(','));
    const sub = '&su=' + encodeURIComponent('Upcoming Regents Prep Activities');
    const body = '&body=' + encodeURIComponent(this.getBody(supportsByActivityLead));
    return base + to + sub + body;
  }

  getBody(supportsByActivityLead) {
    let body =
      'Hi team,\n\n' +
      'For this round of Regents Prep we will be using a new tool to help us manage and monitor ' +
      'our Regents Prep Program.  Because you are leading one or more Regents prep ' +
      'activities you will be taking attendance and monitoring student participation ' +
      'using the New Visions Data Portal.\n\n' +
      'The activities that you are leading are listed below along with when they occur.\n';
    _.each(supportsByActivityLead, (value: any, key) => {
      const email = key;
      const supports = value.supports;
      body = body.concat('\n' + email + '\n');
      _.each(supports, (support: any) => {
        const repeatsOnString = support.getMeetsOnScheduleString(support.schedule);
        const startHuman = this.UtilitiesService.convertISODateToHumanDate(support.schedule.startsOn);
        const fullScheduleString = '  -' + support.name + ': ' + repeatsOnString + ' starting on ' + startHuman + '\n';
        body = body.concat(fullScheduleString);
      });
    });
    body =
      body +
      '\nYou can view your rosters and take attendance at: ' +
      this.mySupportsUrl +
      '.  ' +
      'Please bookmark this URL as it is the one you will use to take attendance for all ' +
      'your Regents prep activities as a part of this pilot.  On [ENTER DATE], we will be hosting ' +
      'a staff training focused on taking attendance.\n\n' +
      'Thanks in advance for helping to make this round of Regents prep a success!\n\n\n' +
      'PS. The attendance taking features are mobile friendly.  Try logging in on your phone!';
    return body;
  }

  $onDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
